import type { ColumnOrderState, ColumnSizingState, RowSelectionState, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";

const DEFAULT_VISIBILITY: VisibilityState = {
  name: true,
  recentMessage: true,
  group: true,
  status: true,
  dutyStatus: true,
  location: true,
  volume: true,
  country: false,
  stateProvince: false,
};

function migrateHideGeoColumns(workspaceId: string): void {
  if (!workspaceId) return;
  try {
    const migrationKey = `${workspaceId}-driverGridColumnsMigration`;
    if (localStorage.getItem(migrationKey) === "1") return;

    const storageKey = `${workspaceId}-driverGridColumns`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      parsed.country = false;
      parsed.stateProvince = false;
      localStorage.setItem(storageKey, JSON.stringify(parsed));
    }
    localStorage.setItem(migrationKey, "1");
  } catch {
    // localStorage may be unavailable, at quota, or hold corrupted data —
    // useLocalStorage will fall back to the default visibility state.
  }
}

const BASE_PINNED_COLUMNS = ["_select", "inbox"] as const;
const NAME_COLUMN = "name" as const;

function pinnedColumnIds(leadingTagColumnId: string | null): string[] {
  return leadingTagColumnId
    ? [...BASE_PINNED_COLUMNS, leadingTagColumnId, NAME_COLUMN]
    : [...BASE_PINNED_COLUMNS, NAME_COLUMN];
}

function ensurePinnedColumns(order: ColumnOrderState, pinned: string[]): ColumnOrderState {
  if (order.length === 0) return order;
  const draggable = order.filter((id) => !pinned.includes(id));
  return [...pinned, ...draggable];
}

export function useDriverGridState(workspaceId: string, leadingTagColumnId: string | null = null) {
  migrateHideGeoColumns(workspaceId);

  const [columnVisibility, setColumnVisibility] = useSafeLocalStorage<VisibilityState>(
    `${workspaceId}-driverGridColumns`,
    DEFAULT_VISIBILITY,
  );

  const [storedColumnOrder, setColumnOrder] = useSafeLocalStorage<ColumnOrderState>(
    `${workspaceId}-driverGridColumnOrder`,
    [],
  );
  const columnOrder = useMemo(
    () => ensurePinnedColumns(storedColumnOrder, pinnedColumnIds(leadingTagColumnId)),
    [storedColumnOrder, leadingTagColumnId],
  );

  const [columnSizing, setColumnSizing] = useSafeLocalStorage<ColumnSizingState>(
    `${workspaceId}-driverGridColumnSizing`,
    {},
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const onColumnVisibilityChange = useCallback(
    (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
      setColumnVisibility(typeof updater === "function" ? updater(columnVisibility) : updater);
    },
    [columnVisibility, setColumnVisibility],
  );

  const onColumnOrderChange = useCallback(
    (updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)) => {
      setColumnOrder(typeof updater === "function" ? updater(columnOrder) : updater);
    },
    [columnOrder, setColumnOrder],
  );

  const onColumnSizingChange = useCallback(
    (updater: ColumnSizingState | ((prev: ColumnSizingState) => ColumnSizingState)) => {
      setColumnSizing(typeof updater === "function" ? updater(columnSizing) : updater);
    },
    [columnSizing, setColumnSizing],
  );

  const onRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      setRowSelection((prev) => (typeof updater === "function" ? updater(prev) : updater));
    },
    [],
  );

  return {
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    columnSizing,
    onColumnSizingChange,
    rowSelection,
    onRowSelectionChange,
  };
}
